import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useMediaQuery, useTheme} from '@mui/material';
import {styled} from '@mui/system';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import {format, parseISO} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {EventPosition, SYSTEM_MESSAGE_TYPES} from '@/types/matrix';
import {useAppSelector} from '@/redux-store/store';
import Linkify from 'linkify-react';
import {MatrixEvent} from 'matrix-js-sdk';
import MessageOptionsList from '@/components/matrix-chat/ui/menu-options-list';

import {useMatrixRoomContext} from '@/contexts/matrix/room';
import RenderWithOptionalTooltip from '@/components/ui/optional-tooltip';
import {useMatrixContext} from '@/contexts/matrix/client';
import SenderName from './sender-name';
import IconPanel from '@/components/matrix-chat/ui/icon-panel';
import {translateMessage} from '@/services/matrix/messages/';

const borderRadius = {
  single: {
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px'
  },
  first: {
    borderTopRightRadius: '18px',
    borderBottomRightRadius: '8px',
    borderTopLeftRadius: '18px',
    borderBottomLeftRadius: '4px'
  },
  middle: {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
    borderTopLeftRadius: '4px',
    borderBottomLeftRadius: '4px'
  },
  last: {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '18px',
    borderTopLeftRadius: '4px',
    borderBottomLeftRadius: '18px'
  }
};

const borderRadiusOwn = {
  single: {
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px'
  },
  first: {
    borderTopRightRadius: '18px',
    borderBottomRightRadius: '4px',
    borderTopLeftRadius: '18px',
    borderBottomLeftRadius: '8px'
  },
  middle: {
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px'
  },
  last: {
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '18px',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '18px'
  }
};

const marginBottomStyle = {
  single: {
    marginBottom: '0px'
  },
  first: {
    marginBottom: '1px'
  },
  middle: {
    marginBottom: '1px'
  },
  last: {
    marginBottom: '0px'
  }
};

const MessageContainer = styled('div')<{
  isOwnMessage: boolean;
  isDeletedMessage: boolean;
  canDelete: boolean;
  eventPosition: EventPosition;
  isDarkMode: boolean;
}>(({theme, isOwnMessage, isDeletedMessage, eventPosition}) => ({
  margin: theme.spacing(0),
  padding: theme.spacing(1),
  maxWidth: '60%',
  backgroundColor: isOwnMessage
    ? theme.palette.customColors.messageBackgroundOwn
    : theme.palette.customColors.messageBackground,
  color: isOwnMessage
    ? theme.palette.customColors.messageText
    : theme.palette.customColors.messageTextOwn,
  alignSelf: isOwnMessage ? 'flex-end' : 'flex-start',
  fontStyle: isDeletedMessage ? 'italic' : 'normal',
  ...(isOwnMessage
    ? borderRadiusOwn[eventPosition]
    : borderRadius[eventPosition]),
  // ...borderRadius[eventPosition],
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  // cursor: !isOwnMessage || isDeletedMessage ? 'default' : 'pointer',
  ...marginBottomStyle[eventPosition],
  marginRight: isOwnMessage ? 12 : 0,
  marginLeft: isOwnMessage ? 0 : 12,
  position: 'relative',
  display: 'flex',
  flexDirection: 'row'
}));

interface MatrixMessageChipProps {
  content: MatrixEvent;
  onDelete?: () => void;
  onQuote: () => void;
  eventPosition: EventPosition;
}

const formatTime = (dateString: string): string => {
  const date = parseISO(dateString);
  return format(date, 'HH:mm');
};

const MatrixMessageChip: React.FC<MatrixMessageChipProps> = ({
  content,
  onDelete,
  onQuote,
  eventPosition
}) => {
  const {t} = useTranslation();
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const messageContentRef = useRef<HTMLDivElement>(null);

  const [touchStartTime, setTouchStartTime] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const {setActiveMatrixEvent, currentHoveredEvent} = useMatrixRoomContext();

  const {matrixUserId} = useMatrixContext();

  const isDarkMode = useAppSelector((store) => store.theme.darkMode);

  const memberId = matrixUserId;

  const effectiveMatrixEvent = content.getEffectiveEvent();

  const isDeletedMessage = useMemo(() => {
    const isDeleted =
      effectiveMatrixEvent.unsigned &&
      effectiveMatrixEvent.unsigned.redacted_because?.content.reason ===
        'User deleted message';
    return isDeleted;
  }, [effectiveMatrixEvent]);

  const isOwnMessage = effectiveMatrixEvent.sender === memberId;

  const showName = !!(
    (eventPosition === 'first' || eventPosition === 'single') &&
    !isOwnMessage
  );

  const isSystemMessage = SYSTEM_MESSAGE_TYPES.includes(
    effectiveMatrixEvent.type
  );

  const canDeleteMessage =
    isOwnMessage && !isSystemMessage && !isDeletedMessage;

  const showTime = !!(eventPosition === 'last' || eventPosition === 'single');

  const timeColor = isOwnMessage
    ? theme.palette.customColors?.messageText
    : theme.palette.customColors?.messageTextOwn;

  const eventId = effectiveMatrixEvent.event_id;

  const messageText = isDeletedMessage
    ? t('NTF_MESSAGE_DELETED')
    : translateMessage(effectiveMatrixEvent.content.body, t);

  const formattedTime = useMemo(() => {
    const timestamp = new Date(
      effectiveMatrixEvent.origin_server_ts
    ).toISOString();
    return formatTime(timestamp);
  }, [effectiveMatrixEvent]);

  const timePartMargin = isSmallScreen ? {marginLeft: 6} : {marginLeft: 8};

  const timePartFontStyle = isDeletedMessage ? {fontStyle: 'italic'} : {};

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isDeletedMessage) {
        setMenuAnchorEl(event.currentTarget);
        setActiveMatrixEvent(content);
      }
    },
    [content, isDeletedMessage, setActiveMatrixEvent]
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleTouchStart = useCallback(() => {
    if (!isDeletedMessage) {
      setTouchStartTime(Date.now());
    }
  }, [isDeletedMessage]);

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const touchDuration = Date.now() - touchStartTime;
      if (!isDeletedMessage && touchDuration > 500) {
        setMenuAnchorEl(event.currentTarget);
        setActiveMatrixEvent(content);
      }
    },
    [content, isDeletedMessage, setActiveMatrixEvent, touchStartTime]
  );

  const messageBubble = (
    <MessageContainer
      className="matrix-message-element-class"
      data-event-id={eventId}
      ref={messageContentRef}
      style={{flexDirection: 'row'}}
      isOwnMessage={isOwnMessage}
      isDeletedMessage={isDeletedMessage}
      // onClick={(event) => {
      //   isOwnMessage && handleOpenMenu(event);
      // }}
      onContextMenu={(event) => {
        event.preventDefault();
        if (isOwnMessage) {
          handleOpenMenu(event);
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      canDelete={canDeleteMessage}
      eventPosition={eventPosition}
      isDarkMode={isDarkMode}
    >
      {currentHoveredEvent && currentHoveredEvent === eventId && (
        <IconPanel
          onDelete={canDeleteMessage ? onDelete : undefined}
          isOwnMessage={isOwnMessage}
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            right: isOwnMessage ? 'auto' : '-40px',
            left: isOwnMessage ? '-40px' : 'auto'
          }}
        />
      )}
      <div>
        {showName && (
          <SenderName
            showUserProfileByClick={true}
            senderId={content.getEffectiveEvent().sender}
            additionalMarginBottom={0.5}
          />
        )}
        <ResponsiveTypography
          sx={{
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            flexBasis: 'auto'
          }}
          smallAllSizes={isDeletedMessage ? true : false}
        >
          <Linkify
            options={{
              target: '_blank',
              rel: 'noopener noreferrer',
              attributes: {
                style: {
                  color: theme.palette.customColors?.urlLink
                }
              }
            }}
          >
            {messageText}
          </Linkify>
        </ResponsiveTypography>
      </div>
      {showTime && (
        <div
          style={{
            textAlign: 'right',
            alignSelf: 'flex-end',
            ...timePartMargin
          }}
        >
          <ResponsiveTypography
            smallAllSizes={true}
            sx={{fontWeight: 'bold', color: timeColor, ...timePartFontStyle}}
          >
            {formattedTime}
          </ResponsiveTypography>
        </div>
      )}
    </MessageContainer>
  );

  return (
    <>
      <RenderWithOptionalTooltip
        //shoudShowTooltip={!isSystemMessage && !isDeletedMessage && isOwnMessage}
        shoudShowTooltip={false}
        tooltipText={t('more_options')}
        showArrow
      >
        {messageBubble}
      </RenderWithOptionalTooltip>

      <MessageOptionsList
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        onQuote={onQuote}
        canDelete={canDeleteMessage}
        onDelete={canDeleteMessage ? onDelete : undefined}
      />
    </>
  );
};

export default MatrixMessageChip;
